import { Outlet, Link } from 'react-router-dom';
import { Settings, Moon, Sun } from 'lucide-react';
import { useStore } from '@/store';
import { useState, useEffect } from 'react';
import ShareModal from './ShareModal';

// Determine if it's night time (20:00 - 06:00)
function isNightTime() {
  const hour = new Date().getHours();
  return hour >= 20 || hour < 6;
}

export default function Layout() {
  const { auth, settings, updateSettings } = useStore();
  const [showShare, setShowShare] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  // Apply theme to document
  useEffect(() => {
    const html = document.documentElement;
    
    // Determine actual theme
    let actualTheme = settings.theme;
    if (settings.theme === 'auto') {
      actualTheme = isNightTime() ? 'dark' : 'light';
    }
    
    // Apply theme class
    if (actualTheme === 'dark') {
      html.classList.add('dark');
      setIsDarkMode(true);
    } else {
      html.classList.remove('dark');
      setIsDarkMode(false);
    }
  }, [settings.theme]);
  
  // Update theme every minute for auto mode
  useEffect(() => {
    if (settings.theme !== 'auto') return;
    
    const interval = setInterval(() => {
      const hour = new Date().getHours();
      const shouldBeDark = hour >= 20 || hour < 6;
      setIsDarkMode(shouldBeDark);
      
      // Update document class
      if (shouldBeDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [settings.theme]);
  
  const handleThemeChange = () => {
    if (settings.theme === 'auto') {
      updateSettings({ theme: isDarkMode ? 'light' : 'dark' });
    } else if (settings.theme === 'dark') {
      updateSettings({ theme: 'light' });
    } else {
      updateSettings({ theme: 'auto' });
    }
  };
  
  // Get theme display
  const getThemeDisplay = () => {
    if (settings.theme === 'auto') {
      return isNightTime() ? 'Night' : 'Day';
    }
    return settings.theme === 'dark' ? 'Dark' : 'Light';
  };

  // Detect fullscreen for hiding header
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div className="min-h-screen bg-bg-light dark:bg-bg">
      {/* Header - hidden in fullscreen */}
      {!isFullscreen && (
        <header className="border-b border-gray-100 dark:border-white/5 bg-bg-light-card/30 dark:bg-bg-card/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Description Section */}
            <div className="flex items-center gap-4">
              <img src="/logo.svg" alt="RemoteTime" className="w-12 h-12 flex-shrink-0" />
              <div>
                <Link to="/" className="block">
                  <h1 className="text-base font-semibold text-text-light dark:text-text mb-0.5">
                    Track time zones • Find meeting slots • Stay relaxed
                  </h1>
                </Link>
                <p className="text-xs text-text-light-muted dark:text-text-muted">
                  The essential toolkit for distributed teams working across time zones.
                </p>
              </div>
            </div>
          
          {/* Actions */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <button
              onClick={handleThemeChange}
              className="btn-ghost p-2 flex items-center gap-1 text-xs hidden sm:flex"
              title={`Theme: ${getThemeDisplay()} (click to cycle)`}
            >
              {settings.theme === 'auto' ? (
                <>
                  {isNightTime() ? (
                    <Moon className="w-4 h-4" />
                  ) : (
                    <Sun className="w-4 h-4" />
                  )}
                  <span className="text-text-muted">Auto</span>
                </>
              ) : isDarkMode ? (
                <>
                  <Moon className="w-4 h-4" />
                  <span className="text-text-muted">Dark</span>
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4" />
                  <span className="text-text-muted">Light</span>
                </>
              )}
            </button>
            
            {/* Theme Toggle Mobile */}
            <button
              onClick={handleThemeChange}
              className="btn-ghost p-2 sm:hidden"
              title={`Theme: ${getThemeDisplay()}`}
            >
              {settings.theme === 'auto' ? (
                isNightTime() ? (
                  <Moon className="w-4 h-4" />
                ) : (
                  <Sun className="w-4 h-4" />
                )
              ) : isDarkMode ? (
                <Moon className="w-4 h-4" />
              ) : (
                <Sun className="w-4 h-4" />
              )}
            </button>
            
            <Link
              to="/settings"
              className="btn-ghost p-2"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </Link>
            
            {/* Auth status - minimal, non-intrusive */}
            {auth.isAuthenticated && auth.user && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-300 dark:border-white/10">
                {auth.user.photoURL ? (
                  <img
                    src={auth.user.photoURL}
                    alt={auth.user.displayName}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm">
                    {auth.user.displayName?.charAt(0) || 'U'}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      </header>
      )}
      
      {/* Main content */}
      <main className={isFullscreen ? 'p-0' : 'max-w-7xl mx-auto px-4 py-6'}>
        <Outlet />
      </main>
      
      {/* Modals */}
      {showShare && (
        <ShareModal onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
